import type { ReactNode } from 'react';

// Matches `[label](target)` where the target is the shortest non-whitespace run up to a `)`. The
// backend validator (domains/policies/validate.go, inlineLink) uses the same pattern, so every
// link it accepts is found here at the same position.
const LINK_PATTERN = /\[([^\]]*)\]\((\S+?)\)/g;

const isHttpsUrl = (target: string) => /^https:\/\/\S+$/i.test(target);

/**
 * Returns the index of the `)` that closes a link whose target starts at `targetStart` and whose
 * shortest target ends at `lazyEnd` (the first `)`), so that a URL such as
 * `https://example.com/a_(b)` keeps its balanced parentheses. When the parentheses do not balance
 * before whitespace, a bracket or the end of the line, it returns `lazyEnd`, as the backend reads it.
 * Stopping at brackets means the longer target can never swallow a following link.
 */
const closingParenIndex = (line: string, targetStart: number, lazyEnd: number): number => {
  let depth = 0;
  for (let i = targetStart; i < line.length; i += 1) {
    const char = line[i];
    if (char === '(') depth += 1;
    else if (char === ')') {
      if (depth === 0) return i;
      depth -= 1;
    } else if (/[\s[\]]/.test(char) && i > lazyEnd) return lazyEnd;
  }
  return lazyEnd;
};

/**
 * Renders one line: plain text with `[label](https://...)` links turned into anchors.
 * Anything else, including links with other schemes and image syntax, stays literal text.
 */
const renderLine = (line: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let cursor = 0;

  Array.from(line.matchAll(LINK_PATTERN)).forEach((match) => {
    const [full, label] = match;
    const start = match.index ?? 0;
    const targetStart = start + full.indexOf('](') + 2;
    const end = closingParenIndex(line, targetStart, start + full.length - 1);
    const target = line.slice(targetStart, end);
    const isImage = start > 0 && line[start - 1] === '!';

    if (isImage || !isHttpsUrl(target)) return;

    if (start > cursor) nodes.push(line.slice(cursor, start));
    nodes.push(
      <a key={`${keyPrefix}-${start}`} href={target} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    );
    cursor = end + 1;
  });

  if (cursor < line.length) nodes.push(line.slice(cursor));
  return nodes;
};

/**
 * Renders a policy notice body. The grammar is deliberately tiny: paragraphs separated by blank
 * lines, single newlines as line breaks, and inline https links. Everything else is literal text
 * rendered through React, so raw HTML can never become elements.
 */
const renderNoticeBody = (body: string): ReactNode =>
  body
    .split(/\r?\n[ \t]*(?:\r?\n[ \t]*)+/)
    .filter((paragraph) => paragraph.trim() !== '')
    .map((paragraph, pIndex) => (
      // Paragraphs are static for a given body, so index keys are stable.
      // eslint-disable-next-line react/no-array-index-key
      <p key={pIndex}>
        {paragraph.split(/\r?\n/).map((line, lIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={lIndex}>
            {lIndex > 0 && <br />}
            {renderLine(line, `${pIndex}-${lIndex}`)}
          </span>
        ))}
      </p>
    ));

export default renderNoticeBody;
